import React, { useEffect, useRef, useState } from 'react';
import '../../../styles/mobile-dashboard.css';
import {
  artistAnalyticsService,
  ArtistOverviewData,
} from '../../../services/api/artist-analytics.service';
import { useArtist } from '../../../contexts/ArtistContext';
import { useUser } from '../../../contexts/UserContext';
import {
  AvailableBalanceTile,
  CustomerSupportTile,
  TotalFollowersTile,
  SpotifyListenersTile,
  YouTubeMusicViewsTile,
} from '../OverviewSection';

const MobileHighlights: React.FC = () => {
  const { userProfile } = useUser();
  const { currentArtistId, hasPermission } = useArtist();
  const [overviewData, setOverviewData] = useState<ArtistOverviewData[]>([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const canViewAccountBalance =
    hasPermission('banking:view') || hasPermission('banking:manage');
  const isArtistMapped = userProfile?.artist?.[0]?.is_artist_mapped === true;

  useEffect(() => {
    let cancelled = false;

    const loadOverview = async () => {
      if (!currentArtistId) {
        if (!cancelled) {
          setOverviewData([]);
          setOverviewLoading(false);
        }
        return;
      }

      try {
        setOverviewLoading(true);
        const response = await artistAnalyticsService.getArtistOverview(
          currentArtistId.toString()
        );
        if (!cancelled) {
          setOverviewData(response.data || []);
        }
      } catch {
        if (!cancelled) {
          setOverviewData([]);
        }
      } finally {
        if (!cancelled) {
          setOverviewLoading(false);
        }
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [currentArtistId]);

  const effectiveArtistId = currentArtistId?.toString();
  const cardCount =
    (canViewAccountBalance ? 1 : 0) + (isArtistMapped ? 3 : 0) + 1;

  const updateScrollControls = () => {
    const el = carouselRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      setActiveIndex(0);
      return;
    }
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);

    const firstSlide = el.children[0] as HTMLElement | undefined;
    if (!firstSlide) {
      setActiveIndex(0);
      return;
    }
    const gap = parseFloat(getComputedStyle(el).columnGap || '0');
    const stride = firstSlide.offsetWidth + gap;
    if (stride > 0) {
      const nextIndex = Math.round(el.scrollLeft / stride);
      const maxIndex = Math.max(cardCount - 1, 0);
      setActiveIndex(Math.min(Math.max(nextIndex, 0), maxIndex));
    }
  };

  useEffect(() => {
    updateScrollControls();
    const onResize = () => updateScrollControls();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [cardCount, overviewLoading]);

  const scrollByPage = (direction: 'next' | 'prev') => {
    const el = carouselRef.current;
    if (!el) return;
    const amount = el.clientWidth;
    el.scrollBy({
      left: direction === 'next' ? amount : -amount,
      behavior: 'smooth',
    });
  };

  const scrollToIndex = (index: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const firstSlide = el.children[0] as HTMLElement | undefined;
    if (!firstSlide) return;
    const gap = parseFloat(getComputedStyle(el).columnGap || '0');
    const stride = firstSlide.offsetWidth + gap;
    el.scrollTo({
      left: stride * index,
      behavior: 'smooth',
    });
  };

  return (
    <div className="mobile-highlights-root">
      <div className="mobile-highlights-heading">
        <h2 className="mobile-highlights-title">Today</h2>
        <p className="mobile-highlights-subtitle">Change over last 30 days</p>
      </div>

      <div className="mobile-highlights-controls">
        <button
          type="button"
          className="mobile-highlights-nav-button"
          onClick={() => scrollByPage('prev')}
          disabled={!canScrollLeft}
          aria-label="Previous overview card"
        >
          ‹
        </button>
        <button
          type="button"
          className="mobile-highlights-nav-button"
          onClick={() => scrollByPage('next')}
          disabled={!canScrollRight}
          aria-label="Next overview card"
        >
          ›
        </button>
      </div>

      <div
        ref={carouselRef}
        className="mobile-highlights-carousel"
        onScroll={updateScrollControls}
      >
        {canViewAccountBalance && (
          <div className="mobile-highlights-slide">
            <AvailableBalanceTile artistId={effectiveArtistId} isLoading={overviewLoading} />
          </div>
        )}
        {isArtistMapped && (
          <>
            <div className="mobile-highlights-slide">
              <TotalFollowersTile overviewData={overviewData} isLoading={overviewLoading} />
            </div>
            <div className="mobile-highlights-slide">
              <SpotifyListenersTile artistId={effectiveArtistId} isLoading={overviewLoading} />
            </div>
            <div className="mobile-highlights-slide">
              <YouTubeMusicViewsTile artistId={effectiveArtistId} isLoading={overviewLoading} />
            </div>
          </>
        )}
        <div className="mobile-highlights-slide">
          <CustomerSupportTile artistId={effectiveArtistId} isLoading={overviewLoading} />
        </div>
      </div>

      <div className="mobile-highlights-dots" aria-label="Overview carousel position">
        {Array.from({ length: cardCount }).map((_, index) => (
          <button
            key={`dot-${index}`}
            type="button"
            className={`mobile-highlights-dot ${activeIndex === index ? 'is-active' : ''}`}
            onClick={() => scrollToIndex(index)}
            aria-label={`Go to overview card ${index + 1}`}
            aria-pressed={activeIndex === index}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileHighlights;
